/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {
  useEffect,
  useState,
  useRef,
  type ReactElement,
  useMemo,
  useContext,
  useCallback
} from 'react'
import { Box, Flex } from '@chakra-ui/react'
import {
  Assets,
  Container,
  Sprite,
  Text,
  TilingSprite,
  TextStyle,
  Texture,
  FillGradient,
  Point
} from 'pixi.js'
import { Application, extend, useTick } from '@pixi/react'
import { connection, RoomCode } from '../../renderer/roomConnector'
import { RoomSettingsContext } from '../+RoomSettingsContext'
import { ErrorModal, ScreenSizeErrorModal } from '../../components/Modals'
import { activities, universalRoomOptions } from '../../activities'
import SideNav from '../../components/SideNav'
import { CRTFilter, OutlineFilter, ColorOverlayFilter } from 'pixi-filters'
import { Display } from 'react-7-segment-display'
import { Howl, Howler } from 'howler'
// import { setGlobalContext_prerender } from 'vike/dist/esm/node/runtime/globalContext'

const version = '2.9.1.6'

const assetUrl = '../.../../../../assets/images/video-armageddon/'

type KnightPNGs = Record<string, string>

const knightPNGs: KnightPNGs = {
  classic: 'knight-solo.png',
  89: 'knight-solo.png',
  xx: 'knight-mm.png'
}

const clamp = (min: number, value: number, max: number): number => {
  return Math.min(Math.max(value, min), max)
}

extend({
  Container,
  Sprite,
  Text,
  TilingSprite
})

let crtSeed = 0

let prevSeconds = -1

let CHANNEL_GLOBAL: any = null
let CHANNEL_LOBBY_GLOBAL: any = null

let emptyRaceTimer = 0

interface PlayerType {
  clientId: string
  position: number
  newPosition: number
  firing: {
    item: string | null
    frames: number
  }
  receiving: Array<{ item: string; frames: number }>
  wallTimer: number
}

let isConnecting = false
// const CHANNEL_GLOBAL: any = null

const modifiedRoomOptions = { ...universalRoomOptions }
modifiedRoomOptions.roomCodeType.type = 'hidden'
modifiedRoomOptions.roomCodeType.value = 'bizhawk'
let searchParams = null
if (typeof window !== 'undefined') {
  searchParams = new URLSearchParams(window.location.search)
}

const initialRoomSettings = {
  activity: activities.videoArmageddon,
  info: <></>,
  options: {
    ...modifiedRoomOptions,

    mode: {
      label: 'Mode',
      type: 'select',
      value: searchParams?.get('mode') ?? '89',
      options: [
        {
          label: 'Classic (3 Players, no Items)',
          value: 'classic'
        },
        {
          label: "'89 (89 Players, Items)",
          value: '89'
        },
        {
          label: 'XX (Coming Soon)',
          value: 'xx'
        }
      ]
    },
    bots: {
      label: 'Bots to Add on Race Start',
      type: 'number',
      value: Number(searchParams?.get('bots') ?? 3),
      min: 0,
      max: 88,
      step: 1
    },
    raceTimer: {
      label: 'Race Timer (In Minutes)',
      type: 'number',
      // value: Number(searchParams?.get('raceTimer') ?? 10 / 200),
      value: 10,
      min: 1,
      max: 240,
      step: 1
    },
    emptyRaceTimer: {
      label: 'Auto restart after X minutes if empty (0 to Disable)',
      type: 'hidden',
      value: Number(searchParams?.get('emptyRaceTimer') ?? 1),
      min: 0,
      max: 240,
      step: 1
    },
    autoCountdown: {
      label: 'Auto Countdown when player joins auto race (Seconds)',
      type: 'hidden',
      value: Number(searchParams?.get('autoCountdown') ?? 100) + 1,
      min: 0,
      max: 240,
      step: 1
    },
    volume: {
      label: 'Audio Volume',
      type: 'number',
      value: 2,
      min: 0,
      max: 10,
      step: 1
    },
    muteRaceMusic: {
      label: 'Mute Race Music',
      type: 'bool',
      value:
        searchParams != null
          ? searchParams.get('muteRaceMusic') !== 'false'
          : true
    },
    autoHideRoomCode: {
      label: 'Hide Room Code when Race starts',
      type: 'bool',
      value: true
    },
    crtStrength: {
      label: 'CRT Effect Strength',
      type: 'number',
      value: 3,
      min: 0,
      max: 10,
      step: 1
    },
    flashReduction: {
      label: 'Reduce "Game Over" flashing',
      type: 'boolean',
      value: true
    }
  }
}

const isDevMode = import.meta.env.MODE === 'development'
const stageDimensionsRef: { current: any | null } = { current: null }

let startSent = false
let countdownSent = 0

const spliceNumber = (n: number): string[] => {
  const s = n.toString()
  const firstPart = s.slice(0, -3).padStart(2, '0')
  const lastThree = s.slice(-3).padStart(3, '0')
  return [firstPart, lastThree]
}

const interpolateColor = (percentage: number, colors: string[]): string => {
  const hex = (x: number): string => {
    x = Math.round(x)
    return x.toString(16).padStart(2, '0')
  }

  const range = 1 / (colors.length - 1)
  let lowerIndex = Math.floor(percentage / range)
  if (lowerIndex >= colors.length - 1) {
    lowerIndex = colors.length - 2
  }
  const fraction = (percentage - range * lowerIndex) / range

  const color1 = colors[lowerIndex]
  const color2 = colors[lowerIndex + 1]

  const r = Math.ceil(
    parseInt(color1.substring(0, 2), 16) * (1 - fraction) +
      parseInt(color2.substring(0, 2), 16) * fraction
  )
  const g = Math.ceil(
    parseInt(color1.substring(2, 4), 16) * (1 - fraction) +
      parseInt(color2.substring(2, 4), 16) * fraction
  )
  const b = Math.ceil(
    parseInt(color1.substring(4, 6), 16) * (1 - fraction) +
      parseInt(color2.substring(4, 6), 16) * fraction
  )

  const interpolatedColor = hex(r) + hex(g) + hex(b)
  return interpolatedColor
}

const Game: React.FC<{
  refreshStage: () => void
  stageDimensions: { width: number; height: number }
  setParentPhase: (phase: string) => void
  setParentTimeLeft: (timeLeft: {
    hours: number
    minutes: number
    seconds: number
  }) => void
  setParentRoomCode: (roomCode: string) => void
  setParentRoomSettings: (roomSettings: any) => void
}> = ({
  refreshStage,
  stageDimensions,
  setParentPhase,
  setParentTimeLeft,
  setParentRoomCode
}) => {
  const [roomCode, setRoomCode] = useState('')
  const autoraceRef = useRef<boolean>(false)
  const { roomSettings, setRoomSettings } = useContext(RoomSettingsContext)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const scoresRef = useRef<any>({})
  const newScoresRef = useRef<any>({})
  const playersRef = useRef<any[]>([])
  const trackRef = useRef<any>(null)
  const cameraRef = useRef<any>(null)
  const audioRef = useRef<any>({})
  const timeLeftRef = useRef<{
    hours: number
    minutes: number
    seconds: number
  }>({
    hours: 0,
    minutes: 0,
    seconds: 0
  })

  useTick((ticker) => {
    ticker.maxFPS = 24
    const delta = ticker.deltaTime
    gameTick(delta)
  })

  const secondsSinceRef = useRef<number>(0)
  const secondsSinceStartRef = useRef<number>(0)
  const countdownRef = useRef<number>(5)

  const modeRef = useRef<string>(initialRoomSettings.options.mode.value)
  const phaseRef = useRef<string>('start')

  const [phase, setPhase] = useState<string>('start')

  const [players, setPlayers] = useState<PlayerType[]>([])

  const [animationFrame, setAnimationFrame] = useState(0)
  const [knightFrame, setKnightFrame] = useState(0)

  const gameoverRef = useRef<any>(null)
  const highscoreRef = useRef<any>(null)

  const [startButtonText, setStartButtonText] = useState<string>('Start')

  const texturesRef = useRef<any>({})

  useEffect(() => {
    const loadTextures = async () => {
      const textures = await Promise.all([
        Assets.load(
          '../.../../../../assets/images/video-armageddon/wall.png'
        ).then((texture: any) => {
          texturesRef.current.wall = texture
        }),
        Assets.load(
          '../.../../../../assets/images/video-armageddon/Overlay.png'
        ).then((texture: any) => {
          texturesRef.current.overlay = texture
        }),
        Assets.load(
          '../.../../../../assets/images/video-armageddon/knight-solo.png'
        ).then((texture: any) => {
          texturesRef.current.knight = texture
        }),
        Assets.load(
          '../.../../../../assets/images/video-armageddon/knight-mm.png'
        ).then((texture: any) => {
          texturesRef.current.knightMM = texture
        }),
        Assets.load(
          '../.../../../../assets/images/video-armageddon/banana.png'
        ).then((texture: any) => {
          texturesRef.current.banana = texture
        }),
        Assets.load(
          '../.../../../../assets/images/video-armageddon/redShell.png'
        ).then((texture: any) => {
          texturesRef.current.redShell = texture
        }),
        Assets.load(
          '../.../../../../assets/images/video-armageddon/greenShell.png'
        ).then((texture: any) => {
          texturesRef.current.greenShell = texture
        }),
        Assets.load(
          '../.../../../../assets/images/video-armageddon/level1.png'
        ).then((texture: any) => {
          texturesRef.current.level1 = texture
        }),
        Assets.load(
          '../.../../../../assets/images/video-armageddon/level2.png'
        ).then((texture: any) => {
          texturesRef.current.level2 = texture
        }),
        Assets.load(
          '../.../../../../assets/images/video-armageddon/streak0.png'
        ).then((texture: any) => {
          texturesRef.current.streak0 = texture
        }),
        Assets.load(
          '../.../../../../assets/images/video-armageddon/streak1.png'
        ).then((texture: any) => {
          texturesRef.current.streak1 = texture
        }),
        Assets.load(
          '../.../../../../assets/images/video-armageddon/streak2.png'
        ).then((texture: any) => {
          texturesRef.current.streak2 = texture
        }),
        Assets.load(
          '../../../../../assets/images/video-armageddon/gameover.mp4'
        ).then((texture: any) => {
          texture.baseTexture.updateFPS = 5
          texture.baseTexture.muted = true
          texture.baseTexture.autoPlay = false
          texturesRef.current.gameover = texture
        })
      ])
    }
    loadTextures()
  }, [])

  const triggerLobbyAction = (message: any): void => {
    if (message.self) return
    switch (message.action) {
      case 'knock':
        CHANNEL_LOBBY_GLOBAL.send({
          type: 'client-message_let_in',
          id: CHANNEL_LOBBY_GLOBAL.myID,
          clientId: message.alias,
          mode: modeRef.current
        })
        break
    }
  }

  const triggerAction = (message: any): void => {
    if (message.self) return
    console.log('triggerAction', message)
    switch (message.action) {
      case 'start':
        if (typeof message.clientId !== 'undefined') return
        for (let i = 0; i < roomSettings?.options.bots.value; i++) {
          const botName = `Bot${i + 1}`
          playersRef.current.push({
            clientId: botName,
            position: 1,
            newPosition: 1,
            firing: {
              item: null,
              frames: 0
            },
            receiving: [],
            wallTimer: 0
          })
          setPlayers([...playersRef.current])
          scoresRef.current[botName] = 0
          sendStandings()
        }
        setPhase('countdown')
        phaseRef.current = 'countdown'
        break
      case 'go':
        sendStart()
        setPhase('racing')
        phaseRef.current = 'racing'
        audioRef.current.countdown.seek(audioRef.current.wait.seek())
        audioRef.current.countdown.play()
        CHANNEL_LOBBY_GLOBAL.close()
        // audioRef.current.blip.pause()
        // audioRef.current.blip.play()

        break
      case 'update':
        console.log('updating score')

        if (scoresRef.current[message.clientId] == null) return
        newScoresRef.current[message.clientId] = Number(message.score)
        console.log(newScoresRef.current)
        break

      case 'throw':
        if (scoresRef.current[message.clientId] == null) return
        if (roomSettings?.options.mode.value === 'classic') return
        console.log('throwing item')
        sendItem(message.item, message.clientId)
        break

      case 'buy':
        if (scoresRef.current[message.clientId] == null) return
        if (roomSettings?.options.mode.value === 'classic') return
        console.log('throwing item')
        pickItem(message.clientId)
        break
      case 'died':
        if (scoresRef.current[message.clientId] == null) return
        hitWall(message.clientId)
        break
    }
  }

  useEffect(() => {
    setParentPhase(phaseRef.current)
  }, [phaseRef.current])

  useEffect(() => {
    setParentTimeLeft(timeLeftRef.current)
  }, [timeLeftRef.current])

  useEffect(() => {
    setParentRoomCode(roomCode)
  }, [roomCode])

  const triggerFlush = (): void => {
    const connectedPlayers = Object.values(CHANNEL_GLOBAL.members.members).map(
      (member: any) => {
        return member.name
      }
    )
    // const activePlayers = [
    //   ...playersRef.current.filter((player: any) =>
    //     connectedPlayers.includes(player.clientId)
    //   )
    // ]
    // if (JSON.stringify(playersRef.current) !== JSON.stringify(activePlayers)) {
    //   console.log('update players')
    //   playersRef.current = activePlayers
    //   setPlayers(activePlayers)
    //   const tempScores: Record<string, any> = {} // Replace index signature with record type annotation
    //   for (let i = 0; i < activePlayers.length; i++) {
    //     if (scoresRef.current[activePlayers[i].clientId] !== undefined) {
    //       tempScores[activePlayers[i].clientId] =
    //         scoresRef.current[activePlayers[i].clientId]
    //     }
    //   }
    //   scoresRef.current = tempScores
    //   // sendStandings()
    // }

    if (
      (connectedPlayers.length === 1 && phaseRef.current === 'racing') ||
      phaseRef.current === 'gameOver'
    ) {
      // 1 = server
      emptyRaceTimer -= 25
      if (emptyRaceTimer <= 0) {
        emptyRaceTimer = 999
        window.location.reload()
      }
    } else {
      emptyRaceTimer = initialRoomSettings.options.emptyRaceTimer.value * 60
    }
  }

  const sendStandings = (): void => {
    console.log('sending standings')
    const sorted = Object.entries(scoresRef.current)
      .sort((a: any, b: any) => {
        return b[1] - a[1]
      })
      .map((pair) => pair[0])
      .join('|')

    CHANNEL_GLOBAL.send({
      id: CHANNEL_GLOBAL.myID,
      action: 'standings',
      clientId: CHANNEL_GLOBAL.myAlias,
      mode: modeRef.current,
      players: sorted,
      final: phaseRef.current === 'gameOver',
      time: `${timeLeftRef.current.hours
        .toString()
        .padStart(2, '0')}:${timeLeftRef.current.minutes
        .toString()
        .padStart(2, '0')}:${timeLeftRef.current.seconds
        .toString()
        .padStart(2, '0')}`
    })
  }
  const sendCountdown = (number: number): void => {
    if (countdownSent !== number) {
      CHANNEL_GLOBAL.send({
        id: CHANNEL_GLOBAL.myID,
        action: 'countdown',
        value: number
      })
      countdownSent = number
    }
  }

  const sendStart = (): void => {
    if (!startSent) {
      CHANNEL_GLOBAL.send({
        id: CHANNEL_GLOBAL.myID,
        action: 'start',
        mode: modeRef.current
      })
      startSent = true
    }
  }

  const hitWall = (clientId: string): void => {
    const attackerIndex = playersRef.current.findIndex(
      (player) => player.clientId === clientId
    )
    if (attackerIndex === -1) return
    playersRef.current[attackerIndex].wallTimer = 5
    playersRef.current[attackerIndex].newPosition = playersRef.current.length
  }

  const bananaShield = (player: any): boolean => {
    const index = player.receiving.findIndex(
      (attack: any) => attack.item !== 'banana'
    )
    if (index !== -1) {
      player.receiving.splice(index, 1)
      return true
    } else {
      return false
    }
  }

  const sendItem = (item: string, attacker: string): void => {
    let target = 'all'

    const sorted = Object.entries(scoresRef.current).sort((a: any, b: any) => {
      return b[1] - a[1]
    })

    const attackerIndex = sorted.findIndex((pair) => pair[0] === attacker)
    const attackingPlayer = playersRef.current.find(
      (player) => player.clientId === attacker
    )
    if (
      attackingPlayer != null &&
      attackingPlayer.firing.item !== 'lightning'
    ) {
      attackingPlayer.firing.item = item
    }

    let targetPlayer = null
    switch (item) {
      case 'lightning':
        break
      case 'banana':
        target =
          sorted[attackerIndex + 1] != null ? sorted[attackerIndex + 1][0] : ''
        if (bananaShield(attackingPlayer)) {
          target = ''
          attackingPlayer.firing.item = null
        }
        break
      default:
        target =
          sorted[attackerIndex - 1] != null ? sorted[attackerIndex - 1][0] : ''
        break
    }
    if (targetPlayer !== '') {
      if (targetPlayer !== 'all') {
        targetPlayer = playersRef.current.find(
          (player) => player.clientId === target
        )
        targetPlayer?.receiving.push({ item, frames: 0 })
      }
      const uid = Date.now().toString()
      CHANNEL_GLOBAL.send({
        id: CHANNEL_GLOBAL.myID,
        uid,
        action: 'item',
        attacker,
        name: target,
        item,
        clientId: CHANNEL_GLOBAL.myAlias
      })
      CHANNEL_GLOBAL.send({
        id: CHANNEL_GLOBAL.myID,
        uid,
        action: 'item',
        attacker,
        name: target,
        item,
        clientId: CHANNEL_GLOBAL.myAlias
      })
    }
  }

  const pickItem = (attacker: string): void => {
    const sorted = Object.entries(scoresRef.current).sort((a: any, b: any) => {
      return b[1] - a[1]
    })

    const attackerIndex = sorted.findIndex((pair) => pair[0] === attacker)
    const playerPercentile = (attackerIndex + 1 / sorted.length) * 100
    const percentileGroup =
      playerPercentile <= 33 ? 0 : playerPercentile <= 66 ? 1 : 2
    const probabilityCharts: Array<
      Array<{ item: string; probability: number }>
    > = [
      [
        { item: 'banana', probability: 0.75 },
        { item: 'mushroom', probability: 0.25 }
      ],
      [
        { item: 'banana', probability: 0.2 },
        { item: 'redShell', probability: 0.1 },
        { item: 'greenShell', probability: 0.3 },
        { item: 'mushroom', probability: 0.2 },
        { item: 'star', probability: 0.2 }
      ],
      [
        { item: 'lightning', probability: 0.05 },
        { item: 'redShell', probability: 0.3 },
        { item: 'greenShell', probability: 0.15 },
        { item: 'mushroom', probability: 0.3 },
        { item: 'star', probability: 0.2 }
      ]
      // Add more charts as needed
    ]

    // Select the appropriate chart based on the attacker's place
    const selectedChart = probabilityCharts[percentileGroup]
    // Calculate the total weight (i.e., sum of all probabilities)
    const totalWeight = selectedChart.reduce(
      (sum: number, item) => sum + item.probability,
      0
    )

    // Generate a random number between 0 and totalWeight
    const randomNum = Math.random() * totalWeight

    let weightSum = 0

    // Find the item that this random value falls into
    const selectedItem = selectedChart.find((item) => {
      weightSum += item.probability
      return randomNum <= weightSum
    })

    // Now selectedItem is the randomly picked item according to the probability distribution

    CHANNEL_GLOBAL.send({
      id: CHANNEL_GLOBAL.myID,
      action: 'get',
      name: attacker,
      item: selectedItem?.item,
      clientId: CHANNEL_GLOBAL.myAlias
    })
  }

  const triggerLobbyEnter = (message: any): void => {
    if (
      playersRef.current.some(
        (player: any) => player.clientId === message.alias
      )
    ) {
      CHANNEL_LOBBY_GLOBAL.send({
        type: 'client-message_let_in',
        id: CHANNEL_LOBBY_GLOBAL.myID,
        clientId: message.alias,
        mode: modeRef.current
      })
      return
    }

    if (message.alias === 'director') {
      return
    }

    if (phaseRef.current !== 'start') {
      CHANNEL_LOBBY_GLOBAL.send({
        type: 'client-message_full',
        id: CHANNEL_GLOBAL.myID,
        clientId: message.alias
      })
      return
    }

    const tempPlayers = [...playersRef.current]

    switch (roomSettings?.options.mode.value) {
      case 'classic':
        if (tempPlayers.length >= 3) {
          CHANNEL_LOBBY_GLOBAL.send({
            type: 'client-message_full',
            id: CHANNEL_GLOBAL.myID,
            clientId: message.alias
          })
          return
        }
        break
      default:
        if (tempPlayers.length >= 89) {
          CHANNEL_LOBBY_GLOBAL.send({
            type: 'client-message_full',
            id: CHANNEL_GLOBAL.myID,
            clientId: message.alias
          })
          return
        }
    }
    CHANNEL_LOBBY_GLOBAL.send({
      type: 'client-message_let_in',
      id: CHANNEL_LOBBY_GLOBAL.myID,
      clientId: message.alias,
      mode: modeRef.current
    })
  }

  const triggerEnter = (message: any): void => {
    if (
      playersRef.current.some(
        (player: any) => player.clientId === message.alias
      )
    ) {
      sendStandings()
      return
    }

    if (message.alias === 'director') {
      return
    }
    if (phaseRef.current !== 'start') {
      CHANNEL_GLOBAL.send({
        type: 'client-message_full',
        id: CHANNEL_GLOBAL.myID,
        clientId: message.alias
      })
      return
    }
    emptyRaceTimer =
      roomSettings?.options.emptyRaceTimer.value > 0
        ? roomSettings?.options.emptyRaceTimer.value * 60
        : 99999

    const tempPlayers = [...playersRef.current]

    switch (roomSettings?.options.mode.value) {
      case 'classic':
        if (tempPlayers.length >= 3) {
          CHANNEL_GLOBAL.send({
            type: 'client-message_full',
            id: CHANNEL_GLOBAL.myID,
            clientId: message.alias
          })
          return
        }
        break
      default:
        if (tempPlayers.length >= 89) {
          CHANNEL_GLOBAL.send({
            type: 'client-message_full',
            id: CHANNEL_GLOBAL.myID,
            clientId: message.alias
          })
          return
        }
    }
    if ((message.alias as string).startsWith('bot')) {
      // Your code here
      tempPlayers.push({
        clientId: message.alias,
        position: 1,
        newPosition: 1,
        firing: {
          item: null,
          frames: 0
        },
        receiving: [],
        wallTimer: 0
      })
      playersRef.current = tempPlayers
      setPlayers(tempPlayers)
    }
    const clientIdExists = playersRef.current.some(
      (player) => player.clientId === message.clientId
    )

    if (clientIdExists) {
      return
    }

    playersRef.current.push({
      clientId: message.alias,
      position: 1,
      newPosition: 1,
      firing: {
        item: null,
        frames: 0
      },
      receiving: [],
      wallTimer: 0
    })
    setPlayers([...playersRef.current])
    scoresRef.current[message.alias] = 0
    newScoresRef.current[message.alias] = 0

    sendStandings()
    if (autoraceRef.current) {
      triggerAction({ action: 'start' })
    }
  }

  const VACallback = (results: any): void => {
    if (results !== false) {
      setRoomCode(results.roomCode)
      setParentRoomCode(results.roomCode)
      CHANNEL_LOBBY_GLOBAL = results.lobbyChannel
      CHANNEL_GLOBAL = results.channel
    } else {
      setErrorMessage('Failed to connect, try refreshing the window')
    }
  }
  useEffect(() => {
    setRoomSettings(initialRoomSettings)
    refreshStage()

    window.addEventListener('resize', () => {
      refreshStage()
    })

    audioRef.current.blip = new Howl({
      src: ['/assets/audio/video-armageddon/blip.mp3'],
      loop: true
    })
    audioRef.current?.wait?.stop()
    audioRef.current.wait = new Howl({
      src: ['/assets/audio/video-armageddon/wt-wait.mp3'],
      loop: true,
      onend: () => {
        if (phaseRef.current === 'start') return
        audioRef.current.wait.loop = false
        audioRef.current.wait.stop()
        audioRef.current.start.play()
      }
    })
    audioRef.current.countdown = new Howl({
      src: ['/assets/audio/video-armageddon/wt-delay-countdown.mp3'],
      loop: false
    })

    audioRef.current.start = new Howl({
      src: ['/assets/audio/video-armageddon/wt-start.mp3'],
      loop: true,
      onend: () => {
        const racePercentage =
          Math.floor(secondsSinceRef.current) /
          (roomSettings?.options.raceTimer.value * 60)
        if (racePercentage < 1 / 3) return
        audioRef.current.start.pause()
        audioRef.current.middle.play()
      }
    })
    audioRef.current.middle = new Howl({
      src: ['/assets/audio/video-armageddon/wt-middle.mp3'],
      loop: true,
      onend: () => {
        const racePercentage =
          Math.floor(secondsSinceRef.current) /
          (roomSettings?.options.raceTimer.value * 60)
        if (racePercentage < 2 / 3) return
        audioRef.current.middle.pause()
        audioRef.current.end.play()
      }
    })
    audioRef.current.end = new Howl({
      src: ['/assets/audio/video-armageddon/wt-end.mp3'],
      loop: true
    })
    audioRef.current.wait.stop()
    audioRef.current.wait.play()

    // const searchParams = new URLSearchParams(window.location.search)

    if (searchParams?.get('autorace') === 'true') {
      autoraceRef.current = true
      countdownRef.current = roomSettings?.options.autoCountdown.value ?? 60
    }
  }, [])

  useEffect(() => {
    if (!isConnecting && roomSettings !== null) {
      isConnecting = true
      connection(
        'va',
        VACallback,
        {
          triggerAction,
          triggerEnter,
          triggerLobbyAction,
          triggerLobbyEnter
        },
        true
      )
    }
    Howler.volume(roomSettings?.options.volume.value / 10)
    audioRef.current.wait.volume(
      roomSettings?.options.muteRaceMusic.value === true ? 0 : 1
    )
    audioRef.current.start.volume(
      roomSettings?.options.muteRaceMusic.value === true ? 0 : 1
    )
    audioRef.current.middle.volume(
      roomSettings?.options.muteRaceMusic.value === true ? 0 : 1
    )
    audioRef.current.end.volume(
      roomSettings?.options.muteRaceMusic.value === true ? 0 : 1
    )
    if (CHANNEL_GLOBAL !== null) {
      sendStandings()
    }
    modeRef.current = roomSettings?.options.mode.value
    if (autoraceRef.current) {
      countdownRef.current = roomSettings?.options.autoCountdown.value ?? 60
    }
  }, [roomSettings])

  useEffect(() => {
    if (
      Math.floor(secondsSinceRef.current) >=
      roomSettings?.options.raceTimer.value * 60
    ) {
      if (phase === 'gameOver') return
      sendStandings()
      setPhase('gameOver')
      phaseRef.current = 'gameOver'
      secondsSinceRef.current = 0
      prevSeconds = -25
      setAnimationFrame(0)
      let adaTint = 0x808080
      let adaFPS = 5
      sendStandings()

      if (roomSettings?.options.flashReduction.value === false) {
        adaTint = 0xffffff
        adaFPS = 60
      }
      gameoverRef.current = texturesRef.current.gameover
      gameoverRef.current.tint = adaTint
      gameoverRef.current.baseTexture.resource.updateFPS = adaFPS
      gameoverRef.current.baseTexture.resource.currentTime = 0
      gameoverRef.current.baseTexture.resource.play()
      gameoverRef.current.baseTexture.resource.addEventListener(
        'ended',
        (event: any) => {
          // event.target.currentTime = 4.25
          // event.target.play()
          gameoverRef.current.tint = 0x000000
        }
      )

      audioRef.current.blip.pause()
      // audioRef.current.alarm.play()

      let name = null
      let score = -Infinity

      for (const key in scoresRef.current) {
        if (scoresRef.current[key] > score) {
          name = key
          score = scoresRef.current[key]
        }
      }

      highscoreRef.current = {
        name,
        score: spliceNumber(score),
        scale: 0,
        alpa: 0
      }
    }
  }, [
    roomSettings?.options.raceTimer.value,
    Math.floor(secondsSinceRef.current)
  ])

  const knightWidth =
    (stageDimensions.width / 1.75) * (3 / clamp(3, players.length, 10))
  const knightHeight = knightWidth * 0.171

  timeLeftRef.current = useMemo(() => {
    if (phaseRef.current === 'gameOver') {
      return { hours: 0, minutes: 0, seconds: 0 }
    }

    let totalSecondsLeft =
      roomSettings?.options.raceTimer.value * 60 -
      Math.floor(secondsSinceRef.current)
    totalSecondsLeft = totalSecondsLeft > 0 ? totalSecondsLeft : 0
    const hours = Math.floor(totalSecondsLeft / 3600)
    const minutes = Math.floor((totalSecondsLeft % 3600) / 60)
    const seconds = totalSecondsLeft % 60
    return { hours, minutes, seconds }
  }, [
    roomSettings?.options.raceTimer.value,
    Math.floor(secondsSinceRef.current)
  ])

  const setPlayerPosition = (): void => {
    Object.entries(scoresRef.current)
      .sort((a: any, b: any) => {
        return b[1] - a[1]
      })
      .forEach((pair, index) => {
        const clientId = pair[0]

        if (
          Number(scoresRef.current[clientId]) <
          Number(newScoresRef.current[clientId])
        ) {
          scoresRef.current[clientId] =
            Number(scoresRef.current[clientId]) + 100
          if (scoresRef.current[clientId] > newScoresRef.current[clientId]) {
            scoresRef.current[clientId] = Number(newScoresRef.current[clientId])
          }
        }
        if (clientId.startsWith('Bot')) {
          let coinFlip = Math.random() < 0.04 // Returns true (heads) or false (tails)
          if (coinFlip) {
            scoresRef.current[clientId] = Math.floor(
              Number(scoresRef.current[clientId]) +
                (100 * (playersRef.current.length - index)) /
                  playersRef.current.length
            )

            coinFlip = Math.random() < 0.003 // Returns true (heads) or false (tails)
            if (coinFlip && modeRef.current === '89') {
              const random = Math.random()
              if (random < 1 / 10) {
                sendItem('lightning', clientId)
              } else if (random < 2 / 5) {
                sendItem('banana', clientId)
              } else if (random < 3 / 5) {
                sendItem('green', clientId)
              } else if (random < 4 / 6) {
                sendItem('redShell', clientId)
              } else if (random < 5 / 6) {
                sendItem('banana', clientId)
              } else {
                hitWall(clientId)
              }
            }
          }
        }
        const player: PlayerType = playersRef.current.find(
          (player: PlayerType) => player.clientId === clientId
        )
        if (player === undefined) return
        if (player.wallTimer <= 0) {
          player.newPosition = index
        } else {
          player.newPosition = playersRef.current.length
        }
        player.position += (player.newPosition - player.position) / 100
      })
  }

  const gameTick = useCallback(
    (delta: number): void => {
      crtSeed += delta / 60
      setAnimationFrame(crtSeed)

      if (cameraRef.current != null && trackRef.current != null) {
        if (cameraRef.current.tiltDirection == null) {
          cameraRef.current.tiltDirection = 'down'
        }
        if (
          cameraRef.current.height >
          stageDimensionsRef.current.height * 0.7
        ) {
          if (cameraRef.current.tiltDirection === 'down') {
            if (
              Number(cameraRef.current.y) + Number(cameraRef.current.height) <
              stageDimensionsRef.current.height * 0.5
            ) {
              cameraRef.current.tiltDirection = 'up'
            } else {
              cameraRef.current.y -=
                delta * clamp(1, playersRef.current.length / 7, 4)
            }
          } else {
            if (cameraRef.current.y > stageDimensionsRef.current.height * 0.2) {
              cameraRef.current.tiltDirection = 'down'
            } else {
              cameraRef.current.y =
                Number(cameraRef.current.y) +
                delta * clamp(1, playersRef.current.length / 7, 4)
            }
          }
        }
      }

      playersRef.current.forEach((player: PlayerType): void => {
        if (player.firing.item != null) {
          player.firing.frames += delta / 60
          if (player.firing.frames > 0.5) {
            player.firing.item = null
            player.firing.frames = 0
          }
        }

        if (player.wallTimer > 0) {
          player.wallTimer -= delta / 60
        }

        player.receiving = [
          ...player.receiving
            .filter((a) => {
              return a.frames < 3 * 60
            })
            .map((attack) => {
              attack.frames += delta
              return attack
            })
        ]
      })

      switch (phaseRef.current) {
        case 'start':
          if (Math.floor(secondsSinceStartRef.current) > prevSeconds) {
            prevSeconds = Math.floor(secondsSinceStartRef.current)
            if (prevSeconds % 1 === 0 && CHANNEL_GLOBAL !== null) {
              sendStandings()
            }
          }
          secondsSinceStartRef.current += delta / 60
          break

        case 'countdown':
          countdownRef.current -= delta / 60
          if (Math.floor(countdownRef.current) === 1) {
            setStartButtonText('Go')
            triggerAction({ action: 'go' })
            secondsSinceRef.current = 0
            prevSeconds = -1
          } else {
            setStartButtonText(`${Math.floor(countdownRef.current - 1)}`)
            if (Math.floor(countdownRef.current) <= 4) {
              sendCountdown(Math.floor(countdownRef.current) - 1)
            }
          }
          if (Math.floor(secondsSinceStartRef.current) > prevSeconds) {
            prevSeconds = Math.floor(secondsSinceStartRef.current)
            if (prevSeconds % 1 === 0 && CHANNEL_GLOBAL !== null) {
              sendStandings()
            }
          }
          secondsSinceStartRef.current += delta / 60

          break
        case 'racing':
          if (countdownRef.current > 0) {
            countdownRef.current -= delta / 60
          }
          secondsSinceRef.current += delta / 60
          setAnimationFrame(secondsSinceRef.current)
          setKnightFrame(Math.floor(secondsSinceRef.current * 10) % 3)
          setPlayerPosition()
          break
        case 'gameOver':
          secondsSinceRef.current += delta / 60
          setAnimationFrame(secondsSinceRef.current)
          if (secondsSinceRef.current < 0.6) {
            const scale = secondsSinceRef.current * 25
            // const alpha = secondsSinceRef.current - 0.5 % 1
            highscoreRef.current = {
              ...highscoreRef.current,
              scale: Math.min(4, scale),
              alpha: Math.min(1, scale),
              phase: 'score'
            }
          } else if (secondsSinceRef.current >= 4) {
            highscoreRef.current = {
              ...highscoreRef.current,
              scale: 1,
              alpha: 1,
              phase:
                (secondsSinceRef.current - 4) % 1.5 < 0.75 ? 'name' : 'winner'
            }
          } else {
            highscoreRef.current = {
              ...highscoreRef.current,
              scale: 0,
              alpha: 0,
              phase: 'blank'
            }
          }

          break
        default:
      }

      if (Math.floor(secondsSinceRef.current) > prevSeconds) {
        prevSeconds = Math.floor(secondsSinceRef.current)
        if (
          prevSeconds % 25 === 0 &&
          CHANNEL_GLOBAL !== null &&
          autoraceRef.current
        ) {
          triggerFlush()
        }
        if (
          phaseRef.current === 'racing' &&
          prevSeconds % 2 === 0 &&
          CHANNEL_GLOBAL !== null
        ) {
          sendStandings()
        }
        if (
          phaseRef.current === 'gameOver' &&
          prevSeconds % 1 === 0 &&
          CHANNEL_GLOBAL !== null
        ) {
          sendStandings()
        }
      }
    },
    [phaseRef.current]
  )
  const knightAnimation = knightFrame
  const nameStyleFontSizes =
    (stageDimensions.height / 10) * (3 / clamp(3, players.length, 10))
  const scoreStyleFontSizes =
    (stageDimensions.height / 9) *
    (phaseRef.current === 'gameOver' ? 1 : 3 / clamp(3, players.length, 10))
  const scoreSubStyleFontSizes =
    (stageDimensions.height / 9) *
    (phaseRef.current === 'gameOver' ? 1 : 3 / clamp(3, players.length, 10)) *
    0.6
  const nameStyle = new TextStyle({
    dropShadow: {
      alpha: 1,
      color: '#ffffff'
    },
    fontFamily: 'Helvetica',
    fontSize: nameStyleFontSizes,
    fontStyle: 'italic',
    fontWeight: '600',
    letterSpacing: nameStyleFontSizes * -0.1,
    stroke: {
      width: 1,
      color: '#000000'
    }
  })
  const scoreStyle = new TextStyle({
    fill: '#ffffff',
    dropShadow: {
      alpha: 1,
      color: '#000000'
    },
    fontFamily: 'Helvetica',
    fontSize: scoreStyleFontSizes,
    fontWeight: '600',
    letterSpacing: scoreStyleFontSizes * -0.1,
    stroke: {
      width: 1,
      color: '#000000'
    }
  })
  const scoreSubStyle = new TextStyle({
    fill: '#ffffff',
    dropShadow: {
      alpha: 1,
      color: '#000000'
    },

    fontFamily: 'Helvetica',
    fontSize: scoreSubStyleFontSizes,
    fontWeight: '600',
    letterSpacing: scoreSubStyleFontSizes * -0.1,
    stroke: {
      width: 1,
      color: '#000000'
    }
  })
  const goTopStyle = new TextStyle({
    dropShadow: {
      alpha: 1,
      color: '#ff8138'
    },
    fill: new FillGradient(0, 0, 0, 1)
      .addColorStop(0, '#ff007e')
      .addColorStop(0.5, '#f8e0ec')
      .addColorStop(1, '#ff007e'),
    fontFamily: 'Arial Black',
    fontSize: 350,
    fontWeight: 'bolder',
    stroke: '#ff8138'
  })
  const goBottomStyle = new TextStyle({
    dropShadow: {
      alpha: 1,
      color: '#ff8138'
    },

    fill: new FillGradient(0, 0, 0, 1)
      .addColorStop(0, '#ff8138')
      .addColorStop(0.5, '#ff8138')
      .addColorStop(0, '#ff8138'),
    fontFamily: 'Arial Black',
    fontSize: 350,
    fontWeight: 'bolder',
    stroke: {
      width: 5,
      color: '#ff8138'
    }
  })

  const leadSpace = Math.min(
    stageDimensions.width / 4,
    secondsSinceRef.current * 250
  )

  const crtEffectStrength = (roomSettings?.options.crtStrength.value ?? 1) / 10
  const crtEffect = new CRTFilter({
    curvature: 5 * crtEffectStrength,
    lineWidth: 0.605 * crtEffectStrength,
    lineContrast: 0.235 * crtEffectStrength,
    noise: 0.3 * crtEffectStrength,
    noiseSize: 2.655 * crtEffectStrength,
    vignetting: 0.273 * crtEffectStrength,
    vignettingAlpha: 1 * crtEffectStrength,
    vignettingBlur: 0.324 * crtEffectStrength,
    seed: crtSeed / 60,
    time: knightAnimation / 3
  })

  return (
    <pixiContainer anchor={0} x={0} y={0} filters={[crtEffect]}>
      <pixiTilingSprite
        texture={texturesRef.current.level2}
        width={stageDimensions.width}
        height={stageDimensions.height}
        tilePosition={{ x: 0 - secondsSinceRef.current * 1000, y: 0 }}
        tileScale={{ x: 0.25, y: 0.25 }}
      />
      <pixiSprite
        width={stageDimensions.width}
        height={stageDimensions.height}
        texture={texturesRef.current.start}
        x={stageDimensions.width / -3 - leadSpace * 3}
        y={0}
      />
      <pixiContainer
        ref={trackRef}
        y={Math.floor(stageDimensions.height * 0.15)}
      >
        <pixiContainer ref={cameraRef}>
          {players.map((player, index) => {
            const y = knightHeight / 6 + (index + 1) * knightHeight
            if (
              (index !== 0 &&
                index !== players.length - 1 &&
                Number(cameraRef.current.y) +
                  y +
                  Math.floor(stageDimensions.height * 0.15) <
                  0) ||
              Number(cameraRef.current.y) + y > stageDimensions.height
            ) {
              return null
            }
            const score = spliceNumber(scoresRef.current[player.clientId])
            const playerPosition =
              2 -
              2 * (player.position / playersRef.current.length) +
              Math.min(2, playersRef.current.length - 2) * 0.25

            let interpolatedColor
            if (player.wallTimer > 0) {
              interpolatedColor = '6a99e1'
            } else {
              interpolatedColor = interpolateColor(
                (1 - player.position / playersRef.current.length) *
                  (player.position - player.newPosition > 0.5 ? 1 : 0.5),
                ['6a99e1', 'b27dbd', 'f4639c', 'f5ff00']
              )
            }
            return (
              <pixiContainer key={`player-${index}`}>
                {phase === 'racing' && (
                  <pixiContainer
                    filters={[
                      new OutlineFilter({
                        thickness: 5,
                        color: 0x3350ac
                      }),
                      new ColorOverlayFilter({
                        color: parseInt(interpolatedColor, 16),
                        alpha: 1
                      })
                    ]}
                  >
                    <pixiSprite
                      width={
                        knightWidth *
                        clamp(1.45, playersRef.current.length / 2, 5)
                      }
                      height={knightHeight * 1}
                      anchor={1}
                      texture={
                        texturesRef.current[
                          `streak${(knightAnimation + 1 + index) % 3}`
                        ]
                      }
                      x={
                        knightWidth / 3.125 +
                        leadSpace * playerPosition +
                        knightHeight * 0.6
                      }
                      y={knightHeight / 6 + (index + 1) * knightHeight}
                    />
                  </pixiContainer>
                )}
                {player.wallTimer > 0 && (
                  <pixiSprite
                    height={knightHeight * 1}
                    width={knightHeight * 0.8}
                    anchor={0.5}
                    texture={texturesRef.current.wall}
                    x={
                      knightWidth / 2.5 +
                      leadSpace * playerPosition +
                      knightHeight * 0.6
                    }
                    y={knightHeight / 4 + (index + 0.4) * knightHeight}
                  />
                )}
                <pixiSprite
                  height={knightHeight * 1}
                  width={knightHeight * 1.33}
                  anchor={0.5}
                  texture={
                    texturesRef.current[
                      `knight${roomSettings.options.mode.value ? '' : '-mm'}`
                    ]
                  }
                  rotation={
                    player.wallTimer > 0
                      ? 0
                      : [-0.2, 0, 0.2][(knightAnimation + 1 + index) % 3]
                  }
                  x={knightWidth / 2.5 + leadSpace * playerPosition}
                  y={knightHeight / 4 + (index + 0.4) * knightHeight}
                />

                {player.firing.item !== null && (
                  <pixiSprite
                    height={knightHeight * 0.8}
                    width={knightHeight * 1}
                    anchor={0.5}
                    texture={texturesRef.current[player.firing.item]}
                    rotation={[-0.2, 0, 0.2][(knightAnimation + 1 + index) % 3]}
                    x={
                      knightWidth / 2.5 +
                      leadSpace * playerPosition +
                      player.firing.frames *
                        1000 *
                        2 *
                        (player.firing.item === 'banana' ? -1 : 1)
                    }
                    y={knightHeight / 4 + (index + 0.4) * knightHeight}
                  />
                )}
                {player.receiving.map((attack, itemIndex) => {
                  return (
                    <pixiSprite
                      key={
                        'client' +
                        player.clientId.toString() +
                        'attack' +
                        itemIndex.toString()
                      }
                      height={knightHeight * 0.8}
                      width={knightHeight * 1}
                      anchor={0.5}
                      texture={texturesRef.current[attack.item]}
                      rotation={
                        [-0.2, 0, 0.2][(knightAnimation + 1 + index) % 3]
                      }
                      x={
                        knightWidth / 2.5 +
                        leadSpace * playerPosition -
                        stageDimensions.width *
                          (1 - attack.frames / 180) *
                          (attack.item === 'banana' ? -1 : 1)
                      }
                      y={0 + knightHeight / 4 + (index + 0.4) * knightHeight}
                    />
                  )
                })}

                <pixiText
                  x={knightWidth / 3 + leadSpace * playerPosition}
                  y={knightHeight / 3 + index * knightHeight}
                  anchor={new Point(1, 0)}
                  style={nameStyle}
                  text={player.clientId.toUpperCase()}
                />
                {phase === 'racing' && (
                  <pixiContainer>
                    <pixiText
                      x={stageDimensions.width / 10}
                      y={knightHeight / 3 + index * knightHeight}
                      anchor={new Point(1, 0)}
                      style={scoreStyle}
                      text={score[0]}
                    />
                    <pixiText
                      x={stageDimensions.width / 10}
                      y={knightHeight / 3 + index * knightHeight}
                      anchor={new Point(0, 0)}
                      style={scoreSubStyle}
                      text={score[1]}
                    />
                  </pixiContainer>
                )}
              </pixiContainer>
            )
          })}
        </pixiContainer>
      </pixiContainer>
      {gameoverRef.current !== null && (
        <pixiSprite
          x={0}
          y={0}
          width={stageDimensions.width}
          height={stageDimensions.height}
          texture={gameoverRef.current}
          tint={gameoverRef.current.tint}
          // alpha={gameoverRef.current.alpha}
        />
      )}
      {highscoreRef.current !== null &&
        highscoreRef.current.phase === 'score' && (
          <pixiContainer
            anchor={0.5}
            x={stageDimensions.width / 2.2}
            y={stageDimensions.height / 2}
            scale={highscoreRef.current.scale}
            alpha={highscoreRef.current.alpha}
          >
            <pixiText
              x={0}
              y={0}
              anchor={new Point(0.5, 0.5)}
              style={scoreStyle}
              text={highscoreRef.current.score[0]}
            />
            <pixiText
              x={(stageDimensions.height / 9) * 1.1}
              y={0}
              anchor={new Point(0.5, 0.5)}
              style={scoreSubStyle}
              text={highscoreRef.current.score[1]}
            />
          </pixiContainer>
        )}
      {highscoreRef.current !== null &&
        highscoreRef.current.phase === 'name' && (
          <pixiContainer
            anchor={0.5}
            x={stageDimensions.width / 2}
            y={stageDimensions.height / 2}
            scale={4}
          >
            <pixiText
              anchor={0.5}
              style={scoreStyle}
              text={highscoreRef.current.name.toUpperCase()}
            />
          </pixiContainer>
        )}
      {highscoreRef.current !== null &&
        highscoreRef.current.phase === 'winner' && (
          <pixiContainer
            anchor={0.5}
            x={stageDimensions.width / 2}
            y={stageDimensions.height / 2}
            scale={4}
          >
            <pixiText anchor={0.5} style={scoreStyle} text={'WINNER'} />
          </pixiContainer>
        )}
      {gameoverRef.current !== null && autoraceRef.current && (
        <pixiContainer
          anchor={0.5}
          x={stageDimensions.width / 2}
          y={stageDimensions.height * (3 / 4)}
          scale={0.5}
        >
          <pixiText
            anchor={0.5}
            style={scoreStyle}
            text={`Next Race Starting in ${
              initialRoomSettings.options.emptyRaceTimer.value
            } minute${
              initialRoomSettings.options.emptyRaceTimer.value > 1 ? 's' : ''
            }!`}
          />
        </pixiContainer>
      )}

      {countdownRef.current > 0 && playersRef.current.length > 0 && (
        <pixiContainer
          anchor={0.5}
          x={stageDimensions.width / 2}
          y={stageDimensions.height / 2}
          scale={1}
        >
          <pixiText
            anchor={0.5}
            style={goBottomStyle}
            text={` ${startButtonText} `}
          />
          <pixiText
            anchor={0.5}
            cursor="pointer"
            style={goTopStyle}
            eventMode="dynamic"
            text={startButtonText}
            onMouseOver={(e: any) => {
              e.target.parent.scale.set(1.1)
            }}
            onMouseOut={(e: any) => {
              e.target.parent.scale.set(1)
            }}
            onClick={() => {
              if (playersRef.current.length > 0) {
                triggerAction({
                  action: 'start'
                })
              }
            }}
          />
        </pixiContainer>
      )}
      {phase === 'racing' && playersRef.current.length === 0 && (
        <pixiContainer
          anchor={0.5}
          x={stageDimensions.width / 2}
          y={stageDimensions.height / 2}
          scale={1}
        >
          <pixiText
            anchor={0.5}
            style={nameStyle}
            rotation={[-0.02, 0, 0.02][Math.floor((animationFrame * 3) % 3)]}
            text={`Restarting in ${emptyRaceTimer} seconds...`}
          />
        </pixiContainer>
      )}

      {countdownRef.current > 0 && playersRef.current.length === 0 && (
        <pixiContainer
          anchor={0.5}
          x={stageDimensions.width / 2}
          y={stageDimensions.height / 2}
          scale={1}
        >
          <pixiText
            anchor={0.5}
            style={nameStyle}
            rotation={[-0.02, 0, 0.02][Math.floor((animationFrame * 3) % 3)]}
            text={'Waiting for Players...'}
          />
        </pixiContainer>
      )}

      <pixiSprite
        width={stageDimensions.width}
        height={stageDimensions.height}
        texture={texturesRef.current.overlay}
        x={0}
        y={0}
      />
    </pixiContainer>
  )
}

const styles = {
  container: {
    pos: 'absolute',
    height: '100vdh',
    width: '100%',
    left: 0,
    right: 0,
    bottom: 0,
    top: 0
  },
  grid: {
    pos: 'absolute',
    height: '100%',
    width: '100%',
    left: 0,
    right: 0,
    bottom: 0,
    top: 0
  },
  csv: {
    position: 'fixed',
    bottom: '0.25em',
    left: '0.25em',
    fontSize: '25px',
    zIndex: 1000
  }
}

const Page: React.FC = () => {
  const { roomSettings, setRoomSettings } = useContext(RoomSettingsContext)
  const stageContainerRef = useRef<any>(null)
  const [timeLeft, setTimeLeft] = useState<{
    hours: number
    minutes: number
    seconds: number
  }>({
    hours: 0,
    minutes: 0,
    seconds: 0
  })

  const [loaded, setLoaded] = useState<boolean>(false)

  const [roomCode, setRoomCode] = useState<string>('')

  const [phase, setPhase] = useState<string>('')

  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const [stageDimensions, setStageDimensions] = useState<{
    width: number
    height: number
  }>({
    width: 0,
    height: 0
  })

  useEffect(() => {
    refreshStage()
  }, [
    stageContainerRef?.current?.offsetWidth,
    stageContainerRef?.current?.offsetHeight
  ])

  const refreshStage = (): void => {
    if (stageContainerRef?.current?.offsetWidth != null) {
      stageDimensionsRef.current = {
        width: stageContainerRef.current?.offsetWidth ?? 0,
        height: stageContainerRef.current?.offsetHeight
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      console.log('stageDimensionsRef.current', stageDimensionsRef.current)
      setStageDimensions(stageDimensionsRef.current)
    }
  }

  useEffect(() => {
    setTimeout(() => {
      setLoaded(true)
    }, 1000)
  }, [])

  return (
    <Box sx={styles.container}>
      <Box
        ref={stageContainerRef}
        sx={{
          h: '100%',
          w: '100dvw',
          border: '0px solid rgba(0,0,0,0.25)'
        }}
      >
        {loaded && (
          <Application
            autoStart={true}
            className="stage"
            width={stageDimensions.width}
            height={stageDimensions.height}
            resizeTo={stageContainerRef.current}
            antialias={false}
            // options={{ backgroundColor: new PIXI.Color('#181425').toNumber() }}
          >
            <Game
              refreshStage={refreshStage}
              stageDimensions={stageDimensions}
              setParentPhase={setPhase}
              setParentTimeLeft={setTimeLeft}
              setParentRoomCode={setRoomCode}
              setParentRoomSettings={setRoomSettings}
            />
          </Application>
        )}
        <Flex
          pos={'absolute'}
          top="0"
          right="0"
          bg="blackAlpha.800"
          sx={{
            '.display': {
              p: '20px 0 !important'
            }
          }}
        >
          <Display
            value={timeLeft.hours.toString()}
            count={2}
            backgroundColor="transparent"
            color="#F6E05E"
            height={stageDimensions.height / 10}
            skew={false}
          />
          <Display
            value={timeLeft.minutes.toString()}
            count={2}
            backgroundColor="transparent"
            color="#F6E05E"
            height={stageDimensions.height / 10}
            skew={false}
          />
          <Display
            value={timeLeft.seconds.toString()}
            count={2}
            backgroundColor="transparent"
            color="#F6E05E"
            height={stageDimensions.height / 10}
            skew={false}
          />
        </Flex>

        <ErrorModal
          isOpen={errorMessage !== null}
          onClose={() => {
            setErrorMessage(null)
          }}
        >
          {errorMessage}
        </ErrorModal>
        <ScreenSizeErrorModal />
        <RoomCode
          roomCode={roomCode ?? ''}
          hide={
            roomSettings?.options.autoHideRoomCode.value === true
              ? !['start', 'countdown'].includes(phase)
              : false
          }
        />
        <SideNav />
      </Box>
    </Box>
  )
}

export { Page }
